import React from 'react';
import { FaTrashAlt } from 'react-icons/fa';

/**
 * Main layout container that coordinates modular data view controls (search, filter, sort, chips).
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Control components to render inside the toolbar row (e.g., DataSearch, DataFilter, DataSorting).
 * @param {Function} [props.onReset] - Callback invoked when the Reset button is clicked.
 * @param {boolean} [props.showReset=false] - Explicitly displays the Reset button when true.
 * @param {React.ReactNode} [props.chips] - Sub-row slot for displaying active filter badges (e.g., DataFilterChips).
 * @param {string} [props.className=''] - Additional CSS classes applied to the wrapper container.
 *
 * @example
 * <DataViewControl
 *   onReset={handleReset}
 *   showReset={hasFilters}
 *   chips={<DataFilterChips selectedValues={selected} options={options} onRemove={handleRemove} />}
 * >
 *   <DataSearch placeholder="Search..." onSearchChange={handleSearch} />
 *   <DataFilter filterLabel="Status" options={options} selectedValues={selected} onSelectionChange={setSelected} />
 *   <DataSorting options={sortOptions} sortValue={sort} onSortChange={handleSort} />
 * </DataViewControl>
 */
const DataViewControl = ({
  children,
  onReset,
  showReset = false,
  chips,
  className = ''
}) => {
  const hasResetButton = showReset || typeof onReset === 'function';

  return (
    <div className={`space-y-3 font-sans ${className}`.trim()}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
        <div className="flex flex-wrap items-center gap-3 flex-1">
          {children}
        </div>

        {hasResetButton && (
          <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-red-600 transition-colors shadow-2xs cursor-pointer"
            >
              <FaTrashAlt className="text-[10px]" />
              <span>Reset</span>
            </button>
          </div>
        )}
      </div>

      {chips}
    </div>
  );
};

export default DataViewControl;
